// components/DeviceGroups/DeviceGroupsTab.tsx
import React, { useEffect, useMemo, useState } from 'react';
import DeviceGroupsTable from './DeviceGroupsTable';
import DeleteDeviceGroupModal from './DeleteDeviceGroupModal';
import { Button, message } from 'antd';
import type { DeviceGroup, DeviceGroupFilters as DeviceGroupFiltersType } from '../../types';
import { ReloadOutlined } from '@ant-design/icons';

import AddEditDeviceGroupModal from './AddEditDeviceGroupModal';
import { deleteDeviceGroup, listDeviceGroups } from '@/api/devicegroupService';

interface DeleteModalState {
  isOpen: boolean;
  deviceGroup: DeviceGroup | null;
}

type Order = 'ASC' | 'DESC';
type SortField = 'createdAt' | 'updatedAt' | 'deviceCount';

const DeviceGroupsTab: React.FC = () => {
  const [filters, setFilters] = useState<DeviceGroupFiltersType>({
    searchTerm: '',
    status: ''
  });

  // Add sorting state
  const [sortField, setSortField] = useState<SortField | ''>('');
  const [sortOrder, setSortOrder] = useState<Order>('ASC');

  const [deleteModal, setDeleteModal] = useState<DeleteModalState>({
    isOpen: false,
    deviceGroup: null
  });

  const [upsertOpen, setUpsertOpen] = useState<{
    open: boolean;
    mode: 'add' | 'edit';
    record: DeviceGroup | null;
  }>({ open: false, mode: 'add', record: null });

  // table state
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<DeviceGroup[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [order, setOrder] = useState<Order | undefined>(undefined); // keep for future sort

  const apiQuery = useMemo(() => {
    const q: any = { page, limit: pageSize };
    if (filters.searchTerm) q.q = filters.searchTerm;
    if (sortField) {
      q.sortBy = sortField;
      q.order = sortOrder;
    }
    return q;
  }, [page, pageSize, filters.searchTerm, sortField, sortOrder]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await listDeviceGroups(apiQuery);
      // your API returns { data, pagination }
      setRows(res.data as unknown as DeviceGroup[]);
      setTotal(res.pagination?.totalRecords ?? 0);
    } catch (err: any) {
      message.error(err?.message || 'Failed to load device groups');
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiQuery]);

  const handleAddDeviceGroup = () => {
    setUpsertOpen({ open: true, mode: 'add', record: null });
  };

  const handleViewDeviceGroup = (deviceGroup: DeviceGroup) => {
    // navigate to details page (you asked to trigger a page)
    window.location.href = `/device-management/device-group-details/${deviceGroup.id}`;
  };

  const handleEditDeviceGroup = (deviceGroup: DeviceGroup) => {
    setUpsertOpen({ open: true, mode: 'edit', record: deviceGroup });
  };

  const handleDeleteDeviceGroup = (deviceGroup: DeviceGroup) => {
    setDeleteModal({ isOpen: true, deviceGroup });
  };

  const confirmDeleteDeviceGroup = async () => {
    if (!deleteModal.deviceGroup) return;
    try {
      // id is number in API; coerce if your local type is string
      await deleteDeviceGroup(Number((deleteModal.deviceGroup as any).id));
      message.success('Device group deleted');
      setDeleteModal({ isOpen: false, deviceGroup: null });
      // Re-fetch (if last item of page got removed, shift page left)
      if (rows.length === 1 && page > 1) setPage((p) => p - 1);
      else fetchData();
    } catch (err: any) {
      message.error(err?.message || 'Delete failed');
    }
  };

  // Backend search handler
  const handleSearchName = (searchQuery: string) => {
    setFilters(prev => ({ ...prev, searchTerm: searchQuery }));
    setPage(1); // Reset to first page when searching
  };

  // Backend sort handler
  const handleRequestSort = (field: string, order: 'ascend' | 'descend' | null) => {
    if (order === null) {
      setSortField('');
      setSortOrder('ASC');
    } else {
      setSortField(field as SortField);
      setSortOrder(order === 'ascend' ? 'ASC' : 'DESC');
    }
    setPage(1); // Reset to first page when sorting
  };

  return (
    <div className="space-y-6">
      {/* Action Bar */}
      <div className="flex items-center justify-between">
        <Button type="primary" onClick={handleAddDeviceGroup}>
          Add Device Group
        </Button>
        <Button icon={<ReloadOutlined />} onClick={fetchData} loading={loading}>
          Refresh
        </Button>
      </div>

      {/* Device Groups Table */}
      <DeviceGroupsTable
        deviceGroups={rows} // Use rows directly, not filteredRows
        loading={loading}
        total={total}
        page={page}
        pageSize={pageSize}
        onChangePage={(p, ps) => {
          setPage(p);
          setPageSize(ps);
        }}
        // Add search and sort handlers
        nameSearch={filters.searchTerm}
        onSearchName={handleSearchName}
        onRequestSort={handleRequestSort}
        onViewDeviceGroup={handleViewDeviceGroup}
        onEditDeviceGroup={handleEditDeviceGroup}
        onDeleteDeviceGroup={handleDeleteDeviceGroup}
      />

      {/* Add/Edit Modal */}
      <AddEditDeviceGroupModal
        open={upsertOpen.open}
        mode={upsertOpen.mode}
        initialValues={upsertOpen.record || undefined}
        onCancel={() => setUpsertOpen({ open: false, mode: 'add', record: null })}
        onSuccess={() => {
          setUpsertOpen({ open: false, mode: 'add', record: null });
          fetchData();
        }}
      />

      {/* Delete Confirmation Modal */}
      <DeleteDeviceGroupModal
        isOpen={deleteModal.isOpen}
        deviceGroup={deleteModal.deviceGroup}
        onConfirm={confirmDeleteDeviceGroup}
        onCancel={() => setDeleteModal({ isOpen: false, deviceGroup: null })}
      />
    </div>
  );
};

export default DeviceGroupsTab;
